import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonConfig } from '@zipari/shared-ds-util-button';
import { formatTypes } from '@zipari/shared-ds-util-format';
import { ObjectStatuses } from '@zipari/shared-ds-util-status';
import { PaymentCardConfiguration } from '../../shared/interfaces';
import { AutopayService } from '../../shared/services/autopay.service';
import { FormattingService } from '../../shared/services/formatting.service';
import { getValue } from '../../shared/utils/get-value';
import { BillStatuses } from './enums/bill-statuses.enum';
import { getBillingPeriodDate } from './payment-card.helper';
import { checkInputsForText } from '../../design-system.helper';

@Component({
  selector: 'payment-card',
  templateUrl: './payment-card.component.html',
  styleUrls: ['./payment-card.component.scss'],
})
export class PaymentCardComponent implements OnInit {
  @Input() config: PaymentCardConfiguration;
  @Input() data: any;
  @Input() billStatuses: ObjectStatuses;
  @Input() defaultDetailTab: string;
  @Input() isMakePaymentDisabled: boolean;

  @Output() makePayment = new EventEmitter();

  statuses = BillStatuses;
  isPaymentPillStatusVisible = true;

  constructor(
    private formatService: FormattingService,
    private router: Router,
    public autopayService: AutopayService,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    const [config, data, billStatuses] = checkInputsForText([
      this.config,
      this.data,
      this.billStatuses,
    ]);

    this.config = config;
    this.data = data;
    this.billStatuses = billStatuses;

    const configVisibilityStatus = this.config.statusVisibility;

    for (const status in configVisibilityStatus) {
      if (status === this.data.bill_status.toLowerCase()) {
        this.isPaymentPillStatusVisible = configVisibilityStatus[status];
        break;
      }
    }
  }

  get title(): string {
    return getValue(this.data, this.config.title.prop);
  }

  get details(): object[] {
    return getValue(this.data, this.config.details.prop);
  }

  get autopay(): 'ON' | 'OFF' {
    return getValue(this.data, this.config.autopay.prop) ? 'ON' : 'OFF';
  }

  get amount(): string {
    const amt = getValue(this.data, this.config.amount.prop);

    return this.formatService.restructureValueBasedOnFormat(amt, {
      format: formatTypes.CURRENCY,
    });
  }

  get condition(): string {
    return getValue(this.data, this.config.condition_label.prop);
  }

  get baseUrl(): string {
    return `${this.router.url}/${this.data.id}`;
  }

  get accountDetailUrl(): string {
    return `${this.baseUrl}/${
      this.defaultDetailTab ? this.defaultDetailTab : ''
    }`;
  }

  get autopayUrl(): string {
    return `${this.baseUrl}/autopay`;
  }

  get shouldDisplayAutopay(): boolean {
    return !!this.config.autopay?.label && this.data.is_autopay_eligible;
  }

  get paymentButtonConfig(): ButtonConfig {
    const isPrePayment =
      this.data[this.config.amount.prop] === '0' &&
      this.config.footer.make_pre_payment_button;

    return isPrePayment
      ? this.config.footer.make_pre_payment_button
      : this.config.footer.make_payment_button;
  }

  get billPeriod() {
    if (!this.data) return '';
    if (this.config.billPeriod) {
      return getBillingPeriodDate(
        this.data.billing_period_start_date,
        this.data.billing_period_end_date,
        this.datePipe,
      );
    }
  }

  get billNumber() {
    if (this.config.billNumber) {
      return getValue(this.data, this.config?.billNumber.prop) || '';
    } else {
      return '';
    }
  }

  makePaymentClicked() {
    this.makePayment.emit();
  }

  getConditionLabel() {
    return this.condition;
  }

  getTooltip(tooltipMsg: string) {
    return {
      description: tooltipMsg,
    };
  }
}
